import type { BreadcrumbItem, NotificationItem } from '@/types/router'
/**
 * 应用全局状态模块
 * @description 管理应用的全局状态，如标题、加载状态、设备类型等
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { siteConfig } from '@/config/siteConfig'
import { safeLocalStorage } from '@/utils/storage'

// 主题类型
type ThemeType = 'light' | 'dark' | 'high-contrast' | 'auto'

export const useAppStore = defineStore(
  'app',
  () => {
    // 状态定义
    const title = ref('Mech3D')
    const version = ref('1.0.0')
    const loading = ref(false)
    const collapsed = ref(false)
    const device = ref<'desktop' | 'tablet' | 'mobile'>('desktop')
    const language = ref<'zh-CN' | 'en-US'>('zh-CN')
    const theme = ref<ThemeType>('light')
    const breadcrumbs = ref<BreadcrumbItem[]>([])
    const notifications = ref<NotificationItem[]>([])

    // 计算属性
    const isMobile = computed(() => device.value === 'mobile')
    const isTablet = computed(() => device.value === 'tablet')
    const isDesktop = computed(() => device.value === 'desktop')
    const unreadNotifications = computed(() =>
      notifications.value.filter(item => !item.read),
    )
    const unreadCount = computed(() => unreadNotifications.value.length)

    // 操作方法
    function setTitle(newTitle: string) {
      title.value = newTitle
      document.title = `${newTitle} - ${siteConfig.brand.name}`
    }

    function setLoading(isLoading: boolean) {
      loading.value = isLoading
    }

    function toggleCollapsed() {
      collapsed.value = !collapsed.value
    }

    function setCollapsed(isCollapsed: boolean) {
      collapsed.value = isCollapsed
    }

    function setDevice(deviceType: 'desktop' | 'tablet' | 'mobile') {
      device.value = deviceType

      // 移动端自动收起侧边栏
      if (deviceType === 'mobile') {
        collapsed.value = true
      }
    }

    function setLanguage(lang: 'zh-CN' | 'en-US') {
      language.value = lang
      // 这里可以添加国际化逻辑
    }

    function setTheme(newTheme: ThemeType) {
      theme.value = newTheme
    }

    function setBreadcrumbs(items: BreadcrumbItem[]) {
      breadcrumbs.value = items
    }

    function addBreadcrumb(item: BreadcrumbItem) {
      breadcrumbs.value.push(item)
    }

    function clearBreadcrumbs() {
      breadcrumbs.value = []
    }

    function addNotification(
      notification: Omit<NotificationItem, 'id' | 'timestamp' | 'read'>,
    ) {
      const newNotification: NotificationItem = {
        ...notification,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        read: false,
      }
      notifications.value.unshift(newNotification)

      // 限制通知数量，最多保留50条
      if (notifications.value.length > 50) {
        notifications.value = notifications.value.slice(0, 50)
      }
    }

    function markNotificationAsRead(id: string) {
      const notification = notifications.value.find(item => item.id === id)
      if (notification) {
        notification.read = true
      }
    }

    function markAllNotificationsAsRead() {
      notifications.value.forEach((item) => {
        item.read = true
      })
    }

    function removeNotification(id: string) {
      const index = notifications.value.findIndex(item => item.id === id)
      if (index > -1) {
        notifications.value.splice(index, 1)
      }
    }

    function clearNotifications() {
      notifications.value = []
    }

    // 初始化设备类型检测
    function initDeviceDetection() {
      const checkDevice = () => {
        const width = window.innerWidth
        if (width < 768) {
          setDevice('mobile')
        }
        else if (width < 1024) {
          setDevice('tablet')
        }
        else {
          setDevice('desktop')
        }
      }

      checkDevice()
      window.addEventListener('resize', checkDevice)

      return () => {
        window.removeEventListener('resize', checkDevice)
      }
    }

    return {
      // 状态
      title,
      version,
      loading,
      collapsed,
      device,
      language,
      theme,
      breadcrumbs,
      notifications,

      // 计算属性
      isMobile,
      isTablet,
      isDesktop,
      unreadNotifications,
      unreadCount,

      // 方法
      setTitle,
      setLoading,
      toggleCollapsed,
      setCollapsed,
      setDevice,
      setLanguage,
      setTheme,
      setBreadcrumbs,
      addBreadcrumb,
      clearBreadcrumbs,
      addNotification,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      removeNotification,
      clearNotifications,
      initDeviceDetection,
    }
  },
  {
    persist: {
      key: 'mech3d-app',
      storage: safeLocalStorage,
      paths: ['title', 'version', 'collapsed', 'device', 'theme'],
    },
  },
)
